import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api/client';
import { useAuth } from '../lib/auth/store';
import { Section, SectionEyebrow, SectionTitle } from '../components/ui/Section';
import { Card } from '../components/ui/Card';
import { EmptyState } from '../components/ui/EmptyState';
import { PillButton } from '../components/ui/PillButton';
import { Chip } from '../components/ui/Chip';
import { formatDate } from '../lib/format';
import { todayIso } from '../lib/format';
import { useMemo } from 'react';

export const Route = createFileRoute('/_app/dashboard')({
  component: DashboardPage,
});

const FIXED_AREAS = ['Work', 'Personal', 'Health', 'Social'] as const;

function DashboardPage() {
  const user = useAuth((s) => s.user);
  const settings = useAuth((s) => s.settings);

  const { data: tasksData } = useQuery({
    queryKey: ['tasks', 'all'],
    queryFn: () => api.tasks.list(),
  });
  const { data: focusToday } = useQuery({
    queryKey: ['focus', 'today'],
    queryFn: async () => {
      const list = await api.focus.list();
      const today = todayIso();
      return list.sessions.filter((s) => s.startedAt.slice(0, 10) === today && s.status === 'completed');
    },
  });
  const { data: moodToday } = useQuery({
    queryKey: ['mood', 'today'],
    queryFn: () => api.mood.byDate(todayIso()),
  });

  const tasks = tasksData?.tasks ?? [];
  const focusMinutes = focusToday?.reduce((s, f) => s + (f.actualDuration ?? 0), 0) ?? 0;
  const completedToday = tasks.filter((t) => t.completedAt && t.completedAt.slice(0, 10) === todayIso()).length;
  const openTasks = tasks.filter((t) => t.status === 'in_progress');

  const greeting = useMemo(() => {
    const h = new Date().getHours();
    if (h < 5) return 'Still up';
    if (h < 12) return 'Good morning';
    if (h < 18) return 'Good afternoon';
    return 'Good evening';
  }, []);

  return (
    <>
      <Section surface="stone">
        <p className="mono-label text-muted-slate">DASHBOARD</p>
        <SectionTitle size="section-display" className="mt-3">
          {greeting}{user?.name ? `, ${user.name.split(' ')[0]}` : ''}.
        </SectionTitle>
        <p className="text-body-lg text-muted-slate mt-4 max-w-xl text-balance">
          A quiet place to think about your day, line up what matters, and notice what shifts.
        </p>

        <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-4">
          <SummaryCard label="Tasks done today" value={completedToday} sub="completed" />
          <SummaryCard label="Focus minutes" value={focusMinutes} sub="logged today" />
          <SummaryCard
            label="Mood"
            value={moodToday?.entry ? `${moodToday.entry.rating}/5` : '—'}
            sub={moodToday?.entry?.note ?? 'not logged today'}
          />
        </div>
      </Section>

      <Section>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
          <div className="lg:col-span-2">
            <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
              <div className="min-w-0">
                <SectionEyebrow>TODAY</SectionEyebrow>
                <SectionTitle size="card-heading" className="mt-1">Open tasks</SectionTitle>
              </div>
              <Link to="/todos" className="text-link text-caption shrink-0">View all</Link>
            </div>
            {openTasks.length === 0 ? (
              <EmptyState
                title="Nothing pending."
                description="Start a focus session or add a task to keep your day moving."
                action={<Link to="/todos"><PillButton>Add a task</PillButton></Link>}
              />
            ) : (
              <ul className="border-t border-hairline">
                {openTasks.slice(0, 8).map((t) => (
                  <li key={t.id} className="border-b border-hairline py-4 flex items-center gap-4">
                    <div className="flex-1 min-w-0">
                      <p className="text-body text-ink truncate">{t.title}</p>
                      {t.dueDate && <p className="text-caption text-muted-slate mt-0.5">due {formatDate(t.dueDate)}</p>}
                    </div>
                    <Chip>{t.area}</Chip>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div>
            <SectionEyebrow>AREAS</SectionEyebrow>
            <SectionTitle size="card-heading" className="mt-1">Where you put your attention</SectionTitle>
            <div className="mt-6 flex flex-wrap gap-2">
              {FIXED_AREAS.map((a) => (
                <Chip key={a} tone="neutral">{a}</Chip>
              ))}
              {(settings?.customAreas ?? []).map((a) => (
                <Chip key={a} tone="coral">{a}</Chip>
              ))}
            </div>

            <div className="mt-10">
              <SectionEyebrow>QUICK ACTIONS</SectionEyebrow>
              <ul className="mt-4 flex flex-col gap-2">
                <li><Link to="/focus" className="text-link">Start a focus session</Link></li>
                <li><Link to="/journal" className="text-link">Write a journal entry</Link></li>
                <li><Link to="/mood" className="text-link">Log today's mood</Link></li>
                <li><Link to="/stats" className="text-link">See your weekly insight</Link></li>
              </ul>
            </div>
          </div>
        </div>
      </Section>
    </>
  );
}

function SummaryCard({ label, value, sub }: { label: string; value: number | string; sub?: string }) {
  return (
    <Card variant="default" className="!p-6">
      <p className="field-label">{label}</p>
      <p className="mt-3 text-card-heading font-display text-ink">{value}</p>
      {sub && <p className="mt-1 text-caption text-muted-slate">{sub}</p>}
    </Card>
  );
}
